import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const LoanApplicationGuide = () => {
  return (
    <>
      <Helmet>
        <title>Complete Loan Application Guide - Ursus Victor LLC</title>
        <meta name="description" content="Comprehensive guide to preparing and submitting loan applications. Learn what documents you need and how to complete your application." />
      </Helmet>

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        padding: '5rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <div style={{fontSize: '4rem', marginBottom: '1rem'}}>📚</div>
          <h1 style={{fontSize: '3.5rem', fontWeight: '900', marginBottom: '1.5rem'}}>
            Complete Loan Application Guide
          </h1>
          <p style={{fontSize: '1.3rem', opacity: 0.95, lineHeight: '1.7'}}>
            Everything you need to know about preparing a successful loan application
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{padding: '5rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          {/* Introduction */}
          <div style={{marginBottom: '4rem'}}>
            <h2 style={{fontSize: '2.5rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1.5rem'}}>
              Introduction
            </h2>
            <p style={{fontSize: '1.15rem', color: '#475569', lineHeight: '1.9'}}>
              Applying for a loan can seem overwhelming, but with proper preparation and organization, the process 
              becomes much more manageable. This comprehensive guide walks you through every step of preparing a 
              loan application, from gathering documents to submitting your application.
            </p>
          </div>

          {/* Step 1 */}
          <div style={{marginBottom: '4rem'}}>
            <h3 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem'}}>
              Step 1: Understand Your Loan Needs
            </h3>
            <p style={{fontSize: '1.05rem', color: '#475569', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Before starting your application, clearly define:
            </p>
            <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '2rem'}}>
              <li>How much money you need to borrow</li>
              <li>What you will use the funds for</li>
              <li>How long you need to repay the loan</li>
              <li>What monthly payment you can afford</li>
            </ul>
          </div>

          {/* Step 2 */}
          <div style={{marginBottom: '4rem'}}>
            <h3 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem'}}>
              Step 2: Check Your Credit
            </h3>
            <p style={{fontSize: '1.05rem', color: '#475569', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Review your credit report before applying:
            </p>
            <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '2rem'}}>
              <li>Obtain free credit reports from all three bureaus</li>
              <li>Check for errors and dispute any inaccuracies</li>
              <li>Understand your credit score</li>
              <li>Identify any issues that may need explanation</li>
            </ul>
          </div>

          {/* Step 3 */}
          <div style={{marginBottom: '4rem'}}>
            <h3 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem'}}>
              Step 3: Gather Required Documents
            </h3>
            <p style={{fontSize: '1.05rem', color: '#475569', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Common documents needed for most loan applications:
            </p>
            <div style={{
              background: '#f8fafc',
              padding: '2rem',
              borderRadius: '12px',
              border: '2px solid #e2e8f0'
            }}>
              <h4 style={{fontSize: '1.3rem', fontWeight: '700', color: '#1e40af', marginBottom: '1rem'}}>
                Personal Information
              </h4>
              <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '1.5rem', marginBottom: '2rem'}}>
                <li>Government-issued photo ID</li>
                <li>Social Security card or number</li>
                <li>Proof of address (utility bill, lease agreement)</li>
              </ul>

              <h4 style={{fontSize: '1.3rem', fontWeight: '700', color: '#1e40af', marginBottom: '1rem'}}>
                Income Documentation
              </h4>
              <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '1.5rem', marginBottom: '2rem'}}>
                <li>Recent pay stubs (last 2-3 months)</li>
                <li>W-2 forms (last 2 years)</li>
                <li>Tax returns (last 2 years)</li>
                <li>1099 forms if self-employed</li>
              </ul>

              <h4 style={{fontSize: '1.3rem', fontWeight: '700', color: '#1e40af', marginBottom: '1rem'}}>
                Financial Records
              </h4>
              <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '1.5rem'}}>
                <li>Bank statements (last 3 months)</li>
                <li>Investment account statements</li>
                <li>Asset documentation</li>
                <li>List of current debts and monthly payments</li>
              </ul>
            </div>
          </div>

          {/* Step 4 */}
          <div style={{marginBottom: '4rem'}}>
            <h3 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem'}}>
              Step 4: Complete the Application
            </h3>
            <p style={{fontSize: '1.05rem', color: '#475569', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Tips for completing your application:
            </p>
            <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '2rem'}}>
              <li>Read all instructions carefully before starting</li>
              <li>Answer all questions completely and honestly</li>
              <li>Double-check all information for accuracy</li>
              <li>Ensure dates, amounts, and names are consistent across all documents</li>
              <li>Don't leave any required fields blank</li>
              <li>Provide explanations for any unusual circumstances</li>
            </ul>
          </div>

          {/* Step 5 */}
          <div style={{marginBottom: '4rem'}}>
            <h3 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem'}}>
              Step 5: Review Before Submitting
            </h3>
            <p style={{fontSize: '1.05rem', color: '#475569', lineHeight: '1.8', marginBottom: '1.5rem'}}>
              Before submitting, verify:
            </p>
            <ul style={{fontSize: '1.05rem', color: '#475569', lineHeight: '2', marginLeft: '2rem'}}>
              <li>All forms are completely filled out</li>
              <li>All required documents are included</li>
              <li>Information is accurate and consistent</li>
              <li>Documents are current and not expired</li>
              <li>Everything is legible and properly formatted</li>
              <li>You have copies of everything for your records</li>
            </ul>
          </div>

          {/* Common Mistakes */}
          <div style={{marginBottom: '4rem'}}>
            <h3 style={{fontSize: '2rem', fontWeight: '800', color: '#1e40af', marginBottom: '1.5rem'}}>
              Common Mistakes to Avoid
            </h3>
            <div style={{
              background: '#fef3c7',
              padding: '2rem',
              borderRadius: '12px',
              border: '2px solid #f59e0b'
            }}>
              <ul style={{fontSize: '1.05rem', color: '#78350f', lineHeight: '2', marginLeft: '1.5rem'}}>
                <li>Providing incomplete or inaccurate information</li>
                <li>Missing required documents</li>
                <li>Inconsistent information across documents</li>
                <li>Not explaining credit issues or gaps in employment</li>
                <li>Submitting expired or outdated documents</li>
                <li>Applying for more than you can afford</li>
                <li>Not reviewing the application before submission</li>
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div style={{
            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
            padding: '3rem',
            borderRadius: '16px',
            border: '3px solid #3b82f6',
            textAlign: 'center'
          }}>
            <h3 style={{fontSize: '2rem', fontWeight: '900', color: '#1e3a8a', marginBottom: '1rem'}}>
              Need Professional Help?
            </h3>
            <p style={{fontSize: '1.15rem', color: '#475569', marginBottom: '2rem', lineHeight: '1.7'}}>
              Our experts can guide you through every step of the application process
            </p>
            <Link to="/contact" style={{
              background: '#1e40af',
              color: 'white',
              padding: '1.25rem 3rem',
              borderRadius: '12px',
              fontWeight: '800',
              fontSize: '1.2rem',
              display: 'inline-block'
            }}>
              Get Application Support
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LoanApplicationGuide;
